import React, { useState, useCallback } from 'react';
import {
  ArrowLeft,
  Download,
  FileArchive,
  Search,
  Paperclip,
  FileText,
  Image,
  File,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Calendar,
  Hash,
  SlidersHorizontal,
} from 'lucide-react';
import UserMenu from '../components/UserMenu';
import { apiFetch } from '../utils/api';

/**
 * FreeScout Attachments page — a UI over the backend's FreeScout proxy
 * (routes/freescout.py). A deliberate near-duplicate of
 * ZammadAttachments.js (same two-pane layout: lookup a conversation by
 * number, or date-range search; list every thread attachment, download
 * individually or grab the server-assembled ZIP). The two ticket systems
 * are kept as separate pages by product decision — this file mirrors,
 * not shares.
 *
 * FreeScout vocabulary differs from Zammad's: conversations instead of
 * tickets, threads instead of articles, and attachments download via
 * /conversation/{id}/attachment/{id} — FreeScout attachment ids are
 * unique within a conversation, so no thread id travels in the path.
 * The FreeScout API key never reaches this file — VetFlow's backend
 * holds it and forwards requests, which is why these calls hit
 * /api/freescout/* rather than the FreeScout instance directly.
 *
 * Calls go through apiFetch (utils/api.js), which prefixes
 * REACT_APP_BACKEND_URL and adds the Bearer token.
 */



// ── helpers ────────────────────────────────────────────────────────────────────

function fileIcon(contentType = '') {
  if (contentType.startsWith('image/')) return <Image className="h-4 w-4 text-blue-500" />;
  if (contentType.includes('pdf'))      return <FileText className="h-4 w-4 text-red-500" />;
  return <File className="h-4 w-4 text-gray-400" />;
}

const STATE_COLOURS = {
  open:             'bg-green-100 text-green-800',
  active:           'bg-green-100 text-green-800',
  closed:           'bg-gray-100 text-gray-600',
  pending:          'bg-yellow-100 text-yellow-800',
  spam:             'bg-red-100 text-red-700',
  'new':            'bg-blue-100 text-blue-800',
  'pending reminder': 'bg-orange-100 text-orange-800',
};

function StateBadge({ name }) {
  const cls = STATE_COLOURS[name?.toLowerCase()] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {name || 'Unknown'}
    </span>
  );
}

// ── filter config ─────────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'all',          label: 'All files',    emoji: '📎' },
  { key: 'images',       label: 'Images',       emoji: '🖼️' },
  { key: 'pdf',          label: 'PDFs',         emoji: '📄' },
  { key: 'documents',    label: 'Documents',    emoji: '📝' },
  { key: 'spreadsheets', label: 'Spreadsheets', emoji: '📊' },
];

function matchesFilter(contentType = '', filterKey) {
  if (!filterKey || filterKey === 'all') return true;
  const ct = contentType.toLowerCase();
  if (filterKey === 'images')       return ct.startsWith('image/');
  if (filterKey === 'pdf')          return ct.includes('pdf');
  if (filterKey === 'documents')    return ct.includes('word') || ct.includes('msword') ||
                                           ct.startsWith('text/plain') || ct.includes('rtf');
  if (filterKey === 'spreadsheets') return ct.includes('excel') || ct.includes('spreadsheet') ||
                                           ct === 'text/csv';
  return true;
}

const MIN_SIZE_BYTES = 500 * 1024; // 500 KB

// Maximum INCLUSIVE span allowed by the backend's _validate_range
// (routes/freescout.py) — enforced server-side; mirrored here so the
// pickers can make an invalid range hard to express and handleSearch
// can skip a pointless round trip.
const MAX_RANGE_DAYS = 31;

function FilterBar({ active, onChange, counts, minSize, onMinSizeChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <SlidersHorizontal className="h-4 w-4 text-gray-400 flex-shrink-0" />
      {FILTERS.map(f => {
        const count = counts?.[f.key];
        const isActive = active === f.key;
        return (
          <button
            key={f.key}
            onClick={() => onChange(f.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                        border transition-all ${
              isActive
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'
            }`}
          >
            <span>{f.emoji}</span>
            {f.label}
            {count !== undefined && (
              <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-xs ${
                isActive ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
      <div className="w-px h-4 bg-gray-200 mx-1" />
      <button
        onClick={() => onMinSizeChange(!minSize)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                    border transition-all ${
          minSize
            ? 'bg-amber-500 text-white border-amber-500'
            : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-700'
        }`}
      >
        ⚖️ &gt; 500 KB
      </button>
    </div>
  );
}

function buildFilterCounts(attachments) {
  const counts = { all: attachments.length };
  FILTERS.slice(1).forEach(f => {
    counts[f.key] = attachments.filter(a => matchesFilter(a.content_type, f.key)).length;
  });
  return counts;
}

// ── shared sub-components ──────────────────────────────────────────────────────

function ErrorBox({ message }) {
  return (
    <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-red-800">Error</p>
        <p className="text-sm text-red-600 mt-0.5">{message}</p>
      </div>
    </div>
  );
}

function AttachmentRow({ att, conversationId, downloading, onDownload }) {
  return (
    <div className="flex items-center gap-4 px-6 py-3 hover:bg-gray-50 transition-colors">
      <div className="flex-shrink-0">{fileIcon(att.content_type)}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{att.filename}</p>
        <p className="text-xs text-gray-400">{att.size_label} · {att.content_type}</p>
      </div>
      <button
        onClick={() => onDownload(att, conversationId)}
        disabled={downloading === att.id}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200
                   text-xs font-medium text-gray-600 hover:bg-indigo-50 hover:border-indigo-300
                   hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                   transition-colors flex-shrink-0"
      >
        {downloading === att.id
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Download className="h-3.5 w-3.5" />
        }
        Download
      </button>
    </div>
  );
}

async function triggerDownload(url, filename) {
  const r = await apiFetch(url);
  if (!r.ok) {
    const body = await r.json().catch(() => null);
    throw new Error(body?.detail || `HTTP ${r.status}`);
  }
  const blob = await r.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── SINGLE CONVERSATION MODE ───────────────────────────────────────────────────

function SingleConversationPane() {
  const [conversationInput, setConversationInput] = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [result, setResult]           = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [expanded, setExpanded]       = useState({});
  const [filterKey, setFilterKey]     = useState('all');
  const [minSize, setMinSize]         = useState(false);

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    const num = conversationInput.trim();
    if (!num) return;
    setLoading(true); setError(null); setResult(null); setExpanded({});
    try {
      const r = await apiFetch(`/api/freescout/conversation/${encodeURIComponent(num)}`);
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${r.status}`); }
      const data = await r.json();
      setResult(data);
      if (data.attachments.length) setExpanded({ [data.attachments[0].thread_id]: true });
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [conversationInput]);

  const handleDownload = useCallback(async (att, conversationId) => {
    setDownloading(att.id);
    try {
      await triggerDownload(
        `/api/freescout/conversation/${conversationId}/attachment/${att.id}`,
        att.filename
      );
    } catch (err) { alert(`Download failed: ${err.message}`); }
    finally { setDownloading(null); }
  }, []);

  const handleZip = useCallback(async () => {
    if (!result) return;
    setDownloading('zip');
    try {
      const p = new URLSearchParams();
      if (filterKey && filterKey !== 'all') p.set('filter_type', filterKey);
      if (minSize) p.set('min_size', MIN_SIZE_BYTES);
      const qs = p.toString() ? `?${p}` : '';
      const suffix = [filterKey !== 'all' ? filterKey : '', minSize ? '500kb' : ''].filter(Boolean).join('_');
      await triggerDownload(
        `/api/freescout/conversation/${result.conversation.id}/attachments/zip${qs}`,
        `conversation_${result.conversation.number || result.conversation.id}${suffix ? '_' + suffix : ''}_attachments.zip`
      );
    } catch (err) { alert(`ZIP failed: ${err.message}`); }
    finally { setDownloading(null); }
  }, [result, filterKey, minSize]);

  const allAtts     = result?.attachments || [];
  const filterCounts = buildFilterCounts(allAtts);
  const visibleAtts  = allAtts.filter(a =>
    matchesFilter(a.content_type, filterKey) && (!minSize || a.size >= MIN_SIZE_BYTES)
  );

  const grouped = visibleAtts.reduce((acc, att) => {
    if (!acc[att.thread_id]) acc[att.thread_id] = { subject: att.thread_subject, atts: [] };
    acc[att.thread_id].atts.push(att);
    return acc;
  }, {});

  return (
    <>
      <form onSubmit={handleSearch} className="mb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Conversation Number</label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={conversationInput}
                onChange={e => setConversationInput(e.target.value)}
                placeholder="e.g. 12345"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !conversationInput.trim()}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium
                         hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center gap-2 transition-colors"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Searching…</> : <><Search className="h-4 w-4" />Search</>}
            </button>
          </div>
        </div>
      </form>

      {error && <ErrorBox message={error} />}

      {result && (
        <>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-gray-400">#{result.conversation.number || result.conversation.id}</span>
                  {result.conversation.status && <StateBadge name={result.conversation.status} />}
                </div>
                <h2 className="text-lg font-semibold text-gray-900 truncate">{result.conversation.subject || '(no subject)'}</h2>
                <div className="flex gap-4 mt-1 text-xs text-gray-500">
                  <span>{result.attachments.length} attachment{result.attachments.length !== 1 ? 's' : ''}</span>
                  {result.conversation.created_at && <span>Created {new Date(result.conversation.created_at).toLocaleDateString()}</span>}
                </div>
              </div>
              {allAtts.length > 0 && (
                <button
                  onClick={handleZip}
                  disabled={downloading === 'zip'}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg
                             text-sm font-medium hover:bg-indigo-700 disabled:opacity-60
                             disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  {downloading === 'zip'
                    ? <><Loader2 className="h-4 w-4 animate-spin" />Preparing…</>
                    : <><FileArchive className="h-4 w-4" />{filterKey === 'all' ? 'Download All as ZIP' : `Download ${FILTERS.find(f=>f.key===filterKey)?.label} as ZIP`}</>}
                </button>
              )}
            </div>
          </div>

          {allAtts.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-4">
              <FilterBar active={filterKey} onChange={setFilterKey} counts={filterCounts}
                         minSize={minSize} onMinSizeChange={setMinSize} />
            </div>
          )}

          {allAtts.length === 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <CheckCircle2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">This conversation has no attachments.</p>
            </div>
          )}

          {visibleAtts.length === 0 && allAtts.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <p className="text-gray-500 text-sm">No {FILTERS.find(f=>f.key===filterKey)?.label.toLowerCase()} found in this conversation.</p>
            </div>
          )}

          {Object.entries(grouped).map(([threadId, group]) => (
            <div key={threadId} className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-3 overflow-hidden">
              <button
                onClick={() => setExpanded(p => ({ ...p, [threadId]: !p[threadId] }))}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {expanded[threadId] ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                  <span className="text-sm font-medium text-gray-700 truncate">
                    Thread #{threadId}{group.subject ? ` — ${group.subject}` : ''}
                  </span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-4">{group.atts.length} file{group.atts.length !== 1 ? 's' : ''}</span>
              </button>
              {expanded[threadId] && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {group.atts.map(att => (
                    <AttachmentRow key={att.id} att={att} conversationId={result.conversation.id} downloading={downloading} onDownload={handleDownload} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
}

// ── DATE RANGE MODE ────────────────────────────────────────────────────────────

function today() { return new Date().toISOString().slice(0, 10); }
function weekAgo() {
  const d = new Date(); d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}
// ISO-date arithmetic: 'YYYY-MM-DD' + n days → 'YYYY-MM-DD' (UTC-safe:
// parse as UTC noon so DST edges can't roll the day back).
function addDays(iso, n) {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
// Inclusive span in days between two 'YYYY-MM-DD' strings.
function spanDays(fromIso, toIso) {
  return Math.round((new Date(toIso + 'T12:00:00Z') - new Date(fromIso + 'T12:00:00Z')) / 86400000) + 1;
}

function DateRangePane() {
  const [dateFrom, setDateFrom]       = useState(weekAgo());
  const [dateTo, setDateTo]           = useState(today());
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [result, setResult]           = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [expanded, setExpanded]       = useState({});
  const [filterKey, setFilterKey]     = useState('all');
  const [minSize, setMinSize]         = useState(false);

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    // Client-side guard only — the backend's _validate_range is the
    // real gate. This just avoids a round trip we know will 422.
    if (dateTo < dateFrom) {
      setError(`'To' date (${dateTo}) is before 'From' date (${dateFrom}).`);
      return;
    }
    if (spanDays(dateFrom, dateTo) > MAX_RANGE_DAYS) {
      setError(`Date range is limited to ${MAX_RANGE_DAYS} days; requested ${spanDays(dateFrom, dateTo)} days (${dateFrom} to ${dateTo}).`);
      return;
    }
    setLoading(true); setError(null); setResult(null); setExpanded({});
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const r = await apiFetch(`/api/freescout/attachments/by-date?${params}`);
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${r.status}`); }
      const data = await r.json();
      setResult(data);
      // Auto-expand first conversation
      if (data.conversations.length) setExpanded({ [data.conversations[0].id]: true });
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  const handleDownload = useCallback(async (att, conversationId) => {
    setDownloading(att.id);
    try {
      await triggerDownload(
        `/api/freescout/conversation/${conversationId}/attachment/${att.id}`,
        att.filename
      );
    } catch (err) { alert(`Download failed: ${err.message}`); }
    finally { setDownloading(null); }
  }, []);

  const handleZipAll = useCallback(async () => {
    if (!result) return;
    setDownloading('zip-all');
    try {
      const p = new URLSearchParams({ date_from: result.date_from, date_to: result.date_to });
      if (filterKey && filterKey !== 'all') p.set('filter_type', filterKey);
      if (minSize) p.set('min_size', MIN_SIZE_BYTES);
      const suffix = [filterKey !== 'all' ? filterKey : '', minSize ? '500kb' : ''].filter(Boolean).join('_');
      await triggerDownload(
        `/api/freescout/attachments/by-date/zip?${p}`,
        `freescout_attachments_${result.date_from}_to_${result.date_to}${suffix ? '_' + suffix : ''}.zip`
      );
    } catch (err) { alert(`ZIP failed: ${err.message}`); }
    finally { setDownloading(null); }
  }, [result, filterKey, minSize]);

  const handleConversationZip = useCallback(async (conversation) => {
    setDownloading(`zip-${conversation.id}`);
    try {
      const p = new URLSearchParams();
      if (filterKey && filterKey !== 'all') p.set('filter_type', filterKey);
      if (minSize) p.set('min_size', MIN_SIZE_BYTES);
      const qs = p.toString() ? `?${p}` : '';
      const suffix = [filterKey !== 'all' ? filterKey : '', minSize ? '500kb' : ''].filter(Boolean).join('_');
      await triggerDownload(
        `/api/freescout/conversation/${conversation.id}/attachments/zip${qs}`,
        `conversation_${conversation.number || conversation.id}${suffix ? '_' + suffix : ''}_attachments.zip`
      );
    } catch (err) { alert(`ZIP failed: ${err.message}`); }
    finally { setDownloading(null); }
  }, [filterKey, minSize]);

  return (
    <>
      <form onSubmit={handleSearch} className="mb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
              <input
                type="date" value={dateFrom} max={today()}
                onChange={e => {
                  const v = e.target.value;
                  setDateFrom(v);
                  // Clamp 'To' so the pair can't sit invalid: To may be
                  // at most (From + MAX_RANGE_DAYS - 1) — and never a
                  // future date.
                  const cap = addDays(v, MAX_RANGE_DAYS - 1) < today()
                    ? addDays(v, MAX_RANGE_DAYS - 1) : today();
                  if (dateTo > cap) setDateTo(cap);
                }}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
              <input
                type="date" value={dateTo} min={dateFrom}
                max={addDays(dateFrom, MAX_RANGE_DAYS - 1) < today()
                  ? addDays(dateFrom, MAX_RANGE_DAYS - 1) : today()}
                onChange={e => setDateTo(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !dateFrom || !dateTo}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium
                         hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center gap-2 transition-colors"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Searching…</> : <><Search className="h-4 w-4" />Search</>}
            </button>
          </div>
          <p className="mt-3 text-xs text-gray-400">Searches conversations by creation date. Maximum range: {MAX_RANGE_DAYS} days. Large date ranges may take a few seconds.</p>
        </div>
      </form>

      {error && <ErrorBox message={error} />}

      {result && (
        <>
          {/* Summary bar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{result.conversation_count}</div>
                <div className="text-xs text-gray-500 mt-0.5">conversation{result.conversation_count !== 1 ? 's' : ''} with attachments</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{result.total_attachment_count}</div>
                <div className="text-xs text-gray-500 mt-0.5">total attachment{result.total_attachment_count !== 1 ? 's' : ''}</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-gray-700">{result.date_from}</div>
                <div className="text-xs text-gray-400">to {result.date_to}</div>
              </div>
            </div>
            {result.total_attachment_count > 0 && (
              <button
                onClick={handleZipAll}
                disabled={downloading === 'zip-all'}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg
                           text-sm font-medium hover:bg-indigo-700 disabled:opacity-60
                           disabled:cursor-not-allowed transition-colors"
              >
                {downloading === 'zip-all'
                  ? <><Loader2 className="h-4 w-4 animate-spin" />Building ZIP…</>
                  : <><FileArchive className="h-4 w-4" />{filterKey === 'all' ? 'Download All as ZIP' : `Download ${FILTERS.find(f=>f.key===filterKey)?.label} as ZIP`}</>}
              </button>
            )}
          </div>

          {result.total_attachment_count > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-4">
              <FilterBar
                active={filterKey}
                onChange={setFilterKey}
                counts={buildFilterCounts(result.conversations.flatMap(t => t.attachments))}
                minSize={minSize}
                onMinSizeChange={setMinSize}
              />
            </div>
          )}

          {result.conversation_count === 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <CheckCircle2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No conversations with attachments found in this date range.</p>
            </div>
          )}

          {(() => {
            const visibleConversations = result.conversations.filter(conversation =>
              conversation.attachments.some(a =>
                matchesFilter(a.content_type, filterKey) && (!minSize || a.size >= MIN_SIZE_BYTES)
              )
            );
            if (result.conversation_count > 0 && visibleConversations.length === 0) {
              return (
                <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                  <SlidersHorizontal className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm font-medium">No conversations match the active filter.</p>
                  <p className="text-gray-400 text-xs mt-1">Try changing the file type or removing the size filter.</p>
                </div>
              );
            }
            return visibleConversations.map(conversation => (
            <div key={conversation.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-3 overflow-hidden">
              {/* Conversation header row */}
              <div className="flex items-center gap-3 px-5 py-3">
                <button
                  onClick={() => setExpanded(p => ({ ...p, [conversation.id]: !p[conversation.id] }))}
                  className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-70 transition-opacity"
                >
                  {expanded[conversation.id] ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                  <span className="text-xs font-mono text-gray-400 flex-shrink-0">#{conversation.number || conversation.id}</span>
                  {conversation.status && <StateBadge name={conversation.status} />}
                  <span className="text-sm font-medium text-gray-800 truncate">{conversation.subject}</span>
                </button>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {conversation.created_at && (
                    <span className="text-xs text-gray-400">{new Date(conversation.created_at).toLocaleDateString()}</span>
                  )}
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {conversation.attachment_count} file{conversation.attachment_count !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => handleConversationZip(conversation)}
                    disabled={downloading === `zip-${conversation.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200
                               text-xs font-medium text-gray-600 hover:bg-indigo-50 hover:border-indigo-300
                               hover:text-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {downloading === `zip-${conversation.id}`
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <FileArchive className="h-3.5 w-3.5" />}
                    ZIP
                  </button>
                </div>
              </div>

              {/* Expanded attachment list */}
              {expanded[conversation.id] && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {conversation.attachments.filter(a =>
                    matchesFilter(a.content_type, filterKey) && (!minSize || a.size >= MIN_SIZE_BYTES)
                  ).map(att => (
                    <AttachmentRow key={att.id} att={att} conversationId={conversation.id} downloading={downloading} onDownload={handleDownload} />
                  ))}
                </div>
              )}
            </div>
          ));
          })()}
        </>
      )}
    </>
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────

const FreeScoutAttachments = ({ navigateTo }) => {
  const [mode, setMode] = useState('conversation'); // 'conversation' | 'daterange'

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigateTo('home')}
              className="flex items-center space-x-2 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back</span>
            </button>
            <div className="h-5 w-px bg-gray-300" />
            <div className="flex items-center space-x-2">
              <Paperclip className="h-5 w-5 text-indigo-600" />
              <h1 className="text-lg font-semibold text-gray-900">FreeScout Attachments</h1>
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                FreeScout
              </span>
            </div>
          </div>

          {/* Mode tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            <button
              onClick={() => setMode('conversation')}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                mode === 'conversation'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Hash className="h-3.5 w-3.5" />
              By Conversation
            </button>
            <button
              onClick={() => setMode('daterange')}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                mode === 'daterange'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="h-3.5 w-3.5" />
              By Date Range
            </button>
          </div>

          <UserMenu navigateTo={navigateTo} />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {mode === 'conversation' ? <SingleConversationPane /> : <DateRangePane />}
      </div>
    </div>
  );
};

export default FreeScoutAttachments;
